import React from 'react';
import { injectIntl } from 'react-intl';
import { Grid, hexToRgb, Typography } from '@mui/material';
import { MDRT_CHOICES, THEME_COLOR_PALETTES, getValidThemeColorOption } from '../../../constants/my-details';
import _ from 'lodash';
import { getIsMDRTIntl, getMainProductIntl, getMainServiceIntl, getSeniorityIntl } from '../../../utils/my-details';
import AdviserInfoTagChip from '../AdviserInfoTagChip/AdviserInfoTagChip';
import { tryTranslate } from '../../../utils/locale';
import AdviserInfoCardAbstract from '../AdviserInfoCardAbstract/AdviserInfoCardAbstract';

const OtherAwardsTypoStyle = {
  whiteSpace: 'pre-wrap'
};

const FullWidthStyle = {
  width: '100%'
};

function getCustomizedProductsOrServicesArray(customizedProductsOrServices) {
  if (!customizedProductsOrServices || customizedProductsOrServices.length === 0) return [];
  return customizedProductsOrServices
    .split('#')
    .map(e => e.trim())
    .filter(e => e != '');
}

const AdviserInfoPageBusiness = props => {
  const { intl, adviserInfo, isIntoView } = props;

  const {
    company,
    seniority,
    license,
    shouldShowLicense,
    mainProductsServices,
    customizedProductsOrServices,
    isMDRT,
    otherAwards,
    jobExperience
  } = adviserInfo || {};
  const themeColor = getValidThemeColorOption(adviserInfo);
  const borderColor = THEME_COLOR_PALETTES[themeColor][1];
  const RGB = hexToRgb(borderColor)
    .replace('rgb', 'rgba')
    .replace(')', ', 0.3)');
  const formattedMainProductsServices = mainProductsServices || [];
  const formattedMainProductsServicesTags = formattedMainProductsServices.map(e =>
    tryTranslate(intl, getMainProductIntl(e) || getMainServiceIntl(e))
  );
  const customizedProductsOrServicesTags = getCustomizedProductsOrServicesArray(customizedProductsOrServices);
  const productsOrServicesTags = [...formattedMainProductsServicesTags, ...customizedProductsOrServicesTags];

  return (
    <AdviserInfoCardAbstract
      className="adviser-info-page-business"
      title={intl.formatMessage({ id: 'adv-info-business-title' })}
      adviserInfo={adviserInfo}
      contentHeight="calc(100% - 110px)"
      isIntoView={isIntoView}
    >
      <Grid container wrap="nowrap" sx={{ flexDirection: 'column' }}>
        <Grid
          style={{
            ...FullWidthStyle,
            flexGrow: 1,
            paddingBottom: 34
          }}
          className="content-container"
        >
          <Grid container spacing={3} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            {company && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      💻
                    </Typography>
                    <Typography
                      className="title"
                      style={{ whiteSpace: 'pre-wrap' }}
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'Company' })}
                    </Typography>
                  </Grid>
                  <Grid style={{ maxWidth: '100%' }}>
                    <AdviserInfoTagChip color={borderColor} label={company} isIntoView={isIntoView} />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {seniority && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      🕐
                    </Typography>
                    <Typography
                      className="title"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'seniority' })}
                    </Typography>
                  </Grid>
                  <Grid>
                    <AdviserInfoTagChip
                      color={borderColor}
                      label={tryTranslate(intl, getSeniorityIntl(seniority))}
                      isIntoView={isIntoView}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {license && shouldShowLicense && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      🪪
                    </Typography>
                    <Typography
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'license' })}
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography style={{ whiteSpace: 'pre-line' }}>{license}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid style={FullWidthStyle}>
              <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <Grid>
                  <Typography
                    className="emoji"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    📁
                  </Typography>
                  <Typography
                    className="title"
                    sx={{
                      display: 'inline'
                    }}
                  >
                    {intl.formatMessage({ id: 'main-products-services' })}
                  </Typography>
                </Grid>
                <Grid>
                  {productsOrServicesTags.length > 0 ? (
                    <Grid
                      container
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      {productsOrServicesTags.map(tag => (
                        <Grid key={tag}>
                          <AdviserInfoTagChip color={borderColor} label={tag} isIntoView={isIntoView} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">-</Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            {((isMDRT || []).length > 0 || otherAwards) && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      🏆
                    </Typography>
                    <Typography
                      className="title"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'mdrt-achievement' })}
                    </Typography>
                  </Grid>
                  {(isMDRT || []).length > 0 && (
                    <Grid>
                      <Grid
                        container
                        spacing={1}
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'flex-start'
                        }}
                      >
                        {(isMDRT || []).map(mdrtChoice =>
                          MDRT_CHOICES.includes(mdrtChoice) ? (
                            <Grid key={mdrtChoice}>
                              <AdviserInfoTagChip
                                color={borderColor}
                                label={tryTranslate(intl, getIsMDRTIntl(mdrtChoice))}
                                isIntoView={isIntoView}
                              />
                            </Grid>
                          ) : null
                        )}
                      </Grid>
                    </Grid>
                  )}
                  <Grid>
                    <Typography style={OtherAwardsTypoStyle}>{otherAwards || ''}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {jobExperience && (
              <Grid style={FullWidthStyle}>
                <Grid container spacing={1} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Grid>
                    <Typography
                      className="emoji"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      💼
                    </Typography>
                    <Typography
                      className="title"
                      sx={{
                        display: 'inline'
                      }}
                    >
                      {intl.formatMessage({ id: 'job-experience' })}
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography style={{ whiteSpace: 'pre-wrap' }}>{jobExperience}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </AdviserInfoCardAbstract>
  );
};

export default injectIntl(AdviserInfoPageBusiness);
